import * as React from "react";
import { MemoMessageModal as MessageModal } from "src/components/modals/MessageModal";
import { MemoCashGiftModal as CashGiftModal } from "src/components/modals/CashGiftModal";
import Invitation from "src/components/modals/Invitation";
import YourPass from "src/components/modals/YourPass";
import { MemoWishlist as WishlistModal } from "src/components/modals/WishlistModal";
import { MemoLivehallModal as LivehallModal } from "src/components/modals/LivehallModal";
import SeatingReservation, {
  InvitationData,
} from "src/components/modals/SeatingReservation";
import { IconType } from "@src/utils/icon";
import {
  Currency,
  EventBySlugQuery,
  GetAseobiQuery,
  GetEventStreamersQuery,
  GetForeignAccountDetailsQuery,
  GetForeignAccountDetailsQueryVariables,
  GetLiveScheduleQuery,
  GetWishListItemsQuery,
  GetWishListItemsQueryVariables,
} from "@src/@types/api.d";
import { useQuery } from "@apollo/client";
import {
  GetForeignWalletDetails,
  GetWishListItems,
} from "@src/graphql/queries";
import toast from "react-hot-toast";
import AsoebiModal from "@src/components/AsoebiModal";
import LiveSchedules from "@src/components/LiveSchedules";
import useStore from "@src/store";
import { useRouter } from "next/router";

// Breakpoints for visible item counts
const VISIBLE_ITEMS_CONFIG = {
  phone: 5, // 0 - 480px
  tablet: 5, // 481 - 768px
  desktop: 6, // 769px+
};

const useWindowSize = () => {
  const [windowWidth, setWindowWidth] = React.useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowWidth;
};

const getVisibleItemsCount = (width: number): number => {
  if (width <= 480) return VISIBLE_ITEMS_CONFIG.phone;
  if (width <= 768) return VISIBLE_ITEMS_CONFIG.tablet;
  return VISIBLE_ITEMS_CONFIG.desktop;
};

const RECENTLY_VIEWED_KEY = "floater_recently_viewed";

/** Event slugs that must not show the Cash gift floater action */
const NO_CASH_GIFT_SLUGS = ["dunniwedsolu-2026"];

// Get recently viewed items from localStorage
const getRecentlyViewed = (): string[] => {
  if (typeof window === "undefined") return [];
  try {
    const stored = localStorage.getItem(RECENTLY_VIEWED_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// Save recently viewed item to localStorage
const saveRecentlyViewed = (title: string) => {
  if (typeof window === "undefined") return;
  try {
    const recent = getRecentlyViewed();
    // Remove if already exists, then add to front
    const filtered = recent.filter((t) => t !== title);
    const updated = [title, ...filtered].slice(0, 10); // Keep last 10
    localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(updated));
  } catch {
    // Ignore localStorage errors
  }
};

export type FloaterVariant = "default" | "minimalist";

type Props = {
  variant?: FloaterVariant;
  eventId: number;
  hasWallet?: boolean;
  slugData: EventBySlugQuery | undefined;
  loading?: boolean;
  getEventStreamersData: GetEventStreamersQuery;
  liveSchedules: GetLiveScheduleQuery;
  access_control: boolean;
  qr_code: string;
  accessToken: string | null;
  eventUuid: string | undefined;
  getAsoebiData: GetAseobiQuery | undefined;
  admits: number;
  invitation?: InvitationData | null;
  isInvitedToEvent: boolean;
};

export type DeckType = {
  icon: IconType;
  title: string;
  modal: JSX.Element;
  open: boolean;
  fillScreen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const FLOATER_ICONS: Record<
  FloaterVariant,
  [
    IconType,
    IconType,
    IconType,
    IconType,
    IconType,
    IconType,
    IconType,
    IconType
  ]
> = {
  default: [
    "pass",
    "seating",
    "invitation",
    "wishlist",
    "cashGift",
    "message",
    "aso-ebi",
    "stream",
  ],
  minimalist: [
    "yourpass",
    "seating",
    "invitation",
    "wishlist2",
    "cashgift2",
    "message2",
    "asoebi2",
    "livehall",
  ],
};

export const useFloater = ({
  variant = "default",
  getEventStreamersData,
  eventId,
  slugData,
  liveSchedules,
  qr_code,
  accessToken,
  getAsoebiData,
  eventUuid,
  admits,
  invitation,
  isInvitedToEvent,
  access_control,
}: Props) => {
  const router = useRouter();
  const isPreviewMode = useStore((state) => state.isPreviewMode);
  const icons = FLOATER_ICONS[variant];
  const [openInvitation, setOpenInvitation] = React.useState<boolean>(false);
  const [openWishlist, setOpenWishlist] = React.useState<boolean>(false);
  const [openCashGift, setOpenCashGift] = React.useState<boolean>(false);
  const [openMessage, setOpenMessage] = React.useState<boolean>(false);
  const [openPass, setOpenPass] = React.useState<boolean>(false);
  const [openStream, setOpenStream] = React.useState<boolean>(false);
  const [openAsoebi, setOpenAsoebi] = React.useState<boolean>(false);
  // const [openNotification, setOpenNotification] =
  //   React.useState<boolean>(false);
  const [openSeating, setOpenSeating] = React.useState<boolean>(false);
  const [openGuestLogin, setOpenGuestLogin] = React.useState<boolean>(false);
  // const eventSlug = slugData?.eventBySlug?.slug ?? "";
  const showGuestLogin = false;

  const shouldOpenAsoebiFromLink = React.useMemo(() => {
    const linkType = router.query.type;
    const linkTypes = Array.isArray(linkType) ? linkType : [linkType];

    return linkTypes.some((value) => value?.toLowerCase() === "asoebi");
  }, [router.query.type]);

  React.useEffect(() => {
    if (shouldOpenAsoebiFromLink && isInvitedToEvent) {
      setOpenAsoebi(true);
    }
  }, [isInvitedToEvent, shouldOpenAsoebiFromLink]);

  // const showGuestLogin =
  //   !isPreviewMode &&
  //   !accessToken &&
  //   Boolean(eventUuid) &&
  //   eventSlug !== "meetthebarrys";

  const { data: foreignWalletDetails, loading: _foreignDetailsLoading } =
    useQuery<
      GetForeignAccountDetailsQuery,
      GetForeignAccountDetailsQueryVariables
    >(GetForeignWalletDetails, {
      skip: !eventId,
      variables: {
        eventId: !!eventId ? eventId : 0,
      },
      fetchPolicy: "network-only",
      onError(error) {
        toast.error(
          <p className="toast">{error?.message ?? "An error occured"}</p>
        );
      },
    });

  const foreignDetails = React.useMemo(() => {
    // const res = foreignWalletDetails?.getForeignAccountDetails.find(
    //   (item) => item?.currency === Currency.gpb || Currency.usd
    // );
    const res = foreignWalletDetails?.getForeignAccountDetails
      ?.filter((item) => item?.currency !== Currency.Ngn)
      .filter(
        (item): item is NonNullable<typeof item> =>
          item !== null && item !== undefined
      );
    return res;
  }, [foreignWalletDetails]);

  const { data: getWishListItemsData, loading: getWishListItemsLoading } =
    useQuery<GetWishListItemsQuery, GetWishListItemsQueryVariables>(
      GetWishListItems,
      {
        skip: !eventUuid,
        variables: {
          eventId: `${eventUuid}`,
          pageSize: 1,
        },
      }
    );

  const [isCashGiftSetup, setIsCashGiftSetup] = React.useState(false);

  React.useEffect(() => {
    setIsCashGiftSetup(
      (slugData?.eventBySlug?.available_gift_payment_currencies?.length ?? 0) >
        0
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deck: DeckType[] = [
    {
      icon: icons[0],
      title: "Your Pass",
      modal: (
        <YourPass
          admits={admits}
          setOpen={setOpenPass}
          passImage={slugData?.eventBySlug?.pass_image ?? ""}
          eventSlug={slugData?.eventBySlug?.slug ?? ""}
          qr_code={qr_code}
        />
      ),
      open: openPass,
      setOpen: setOpenPass,
      fillScreen: false,
    },
    {
      icon: icons[1],
      title: "Seating",
      modal: (
        <SeatingReservation invitation={invitation} setOpen={setOpenSeating} />
      ),
      open: openSeating,
      setOpen: setOpenSeating,
      fillScreen: false,
    },
    {
      icon: icons[2],
      title: "Invitation",
      modal: (
        <Invitation
          name={slugData?.eventBySlug?.name ?? ""}
          date={slugData?.eventBySlug?.date ?? ""}
          eventId={eventId}
          eventUuid={`${slugData?.eventBySlug?.uuid}`}
          setOpen={setOpenInvitation}
          access_control={
            slugData?.eventBySlug?.event_preference?.access_control ?? false
          }
        />
      ),
      open: openInvitation,
      setOpen: setOpenInvitation,
      fillScreen: false,
    },
    {
      icon: "wishlist",
      title: "Wishlist",
      modal: (
        <WishlistModal
          eventId={eventId}
          eventUuid={eventUuid}
          setOpen={setOpenWishlist}
          foreignDetails={foreignDetails}
          availableGiftPaymentCurrencies={
            slugData?.eventBySlug?.available_gift_payment_currencies ?? []
          }
        />
      ),
      open: openWishlist,
      setOpen: setOpenWishlist,
      fillScreen: false,
    },
    {
      icon: icons[4],
      title: "Cash gift",
      modal: (
        <CashGiftModal
          foreignDetails={foreignDetails}
          eventId={eventId}
          setOpen={setOpenCashGift}
          eventUuid={eventUuid}
          isCashGiftSetup={isCashGiftSetup}
          availableGiftPaymentCurrencies={
            slugData?.eventBySlug?.available_gift_payment_currencies
          }
        />
      ),
      open: openCashGift,
      setOpen: setOpenCashGift,
      fillScreen: false,
    },
    {
      icon: icons[5],
      title: "Message",
      modal: (
        <MessageModal
          name={slugData?.eventBySlug?.name ?? ""}
          eventId={eventId}
          setOpen={setOpenMessage}
        />
      ),
      open: openMessage,
      setOpen: setOpenMessage,
      fillScreen: false,
    },
    {
      icon: icons[6],
      title: "Aso ebi",
      modal: <AsoebiModal eventUuid={eventUuid} setOpen={setOpenAsoebi} />,
      open: openAsoebi,
      setOpen: setOpenAsoebi,
      fillScreen: false,
    },
    {
      icon: icons[7],
      title: "Live Stream",
      modal:
        // TODO: we'll later change *true* to a logic that checks live hall kind
        true ? (
          <LiveSchedules schedules={liveSchedules} />
        ) : (
          <LivehallModal
            src={
              !!getEventStreamersData.getEventStreamers &&
              getEventStreamersData.getEventStreamers!.length > 0
                ? getEventStreamersData.getEventStreamers![0]?.playback_url
                : ""
            }
            isLive={
              !!getEventStreamersData.getEventStreamers &&
              getEventStreamersData.getEventStreamers!.length > 0
                ? !!getEventStreamersData.getEventStreamers![0]?.is_streaming
                : false
            }
            eventId={eventId}
            setOpen={setOpenStream}
          />
        ),
      open: openStream,
      setOpen: setOpenStream,
      fillScreen: false,
    },
  ];

  const wishlistItems = getWishListItemsData?.getWishListItems ?? [];
  const asoebiData = getAsoebiData?.getAseobi ?? [];

  const INVITED_ONLY_TITLES = [
    "Invitation",
    "Your Pass",
    "Seating",
    "Message",
    "Aso ebi",
  ];

  const filteredDeck = React.useMemo(() => {
    if (isPreviewMode) {
      return deck;
    }

    return deck?.filter((item) => {
      if (!isInvitedToEvent && INVITED_ONLY_TITLES.includes(item.title)) {
        return false;
      }
      if (
        item.title === "Wishlist" &&
        (!isCashGiftSetup || wishlistItems?.length < 1)
      ) {
        return false;
      } else if (
        item.title === "Cash gift" &&
        (NO_CASH_GIFT_SLUGS.includes(slugData?.eventBySlug?.slug ?? "") ||
          (!isCashGiftSetup && foreignDetails?.length === 0))
      ) {
        return false;
      } else if (
        item.title === "Your Pass" &&
        (!accessToken || !access_control || !qr_code)
      ) {
        return false;
      } else if (
        item.title === "Seating" &&
        (!accessToken ||
          !invitation?.seating ||
          invitation.seating.length === 0)
      ) {
        return false;
      } else if (
        item.title === "Aso ebi" &&
        (asoebiData.length === 0 || !accessToken)
      ) {
        return false;
      } else if (
        !accessToken &&
        ["Invitation", "Message", "Store", "Live Stream"].includes(item?.title)
      ) {
        return false;
      }
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deck,
    isPreviewMode,
    wishlistItems,
    invitation,
    isInvitedToEvent,
    slugData?.eventBySlug?.slug,
  ]);

  // Expandable floater logic
  const [isExpanded, setIsExpanded] = React.useState<boolean>(false);
  const [recentlyViewed, setRecentlyViewed] = React.useState<string[]>([]);
  const windowWidth = useWindowSize();
  const visibleCount = getVisibleItemsCount(windowWidth);

  // Load recently viewed from localStorage on mount
  React.useEffect(() => {
    setRecentlyViewed(getRecentlyViewed());
  }, []);

  // Track when a modal is opened
  const trackView = React.useCallback((title: string) => {
    saveRecentlyViewed(title);
    setRecentlyViewed(getRecentlyViewed());
  }, []);

  const toggleExpanded = React.useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  // Sort filtered deck by recently viewed (most recent first)
  const sortedDeck = React.useMemo(() => {
    if (recentlyViewed.length === 0) return filteredDeck;

    return [...filteredDeck].sort((a, b) => {
      const aIndex = recentlyViewed.indexOf(a.title);
      const bIndex = recentlyViewed.indexOf(b.title);

      // Items in recentlyViewed come first, sorted by recency
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex; // Lower index = more recent
      }
      if (aIndex !== -1) return -1; // a is recent, b is not
      if (bIndex !== -1) return 1; // b is recent, a is not
      return 0; // Neither is recent, keep original order
    });
  }, [filteredDeck, recentlyViewed]);

  // Apply live-stream visibility: exclude "Live Stream" when there's no schedule/data or when private and not logged in
  const displayDeck = React.useMemo(() => {
    if (isPreviewMode) {
      return sortedDeck;
    }

    return sortedDeck
      .filter((item) =>
        liveSchedules?.getLiveSchedule?.recordCount ||
        !!getEventStreamersData.getEventStreamers?.[0]?.event?.event_preference
          ?.live_hall
          ? item
          : !["Live Stream"].includes(item.title)
      )
      .filter((item) =>
        !liveSchedules?.getLiveSchedule?.recordCount ||
        (!!getEventStreamersData.getEventStreamers?.[0]?.event?.event_preference
          ?.private &&
          !accessToken)
          ? !["Live Stream"].includes(item.title)
          : item
      )
      .filter((item) =>
        liveSchedules?.getLiveSchedule?.recordCount
          ? item
          : !["Live Stream"].includes(item.title)
      );
  }, [
    sortedDeck,
    liveSchedules,
    getEventStreamersData,
    accessToken,
    isPreviewMode,
  ]);

  // Split items into visible and overflow (using display deck so empty state is correct)
  const { visibleItems, overflowItems, hasOverflow } = React.useMemo(() => {
    const total = displayDeck.length;
    // Account for the "more" button taking up one slot
    const effectiveVisibleCount = visibleCount - 1;
    const needsOverflow = total > visibleCount;

    if (!needsOverflow) {
      return {
        visibleItems: displayDeck,
        overflowItems: [] as DeckType[],
        hasOverflow: false,
      };
    }

    return {
      visibleItems: displayDeck.slice(0, effectiveVisibleCount),
      overflowItems: displayDeck.slice(effectiveVisibleCount),
      hasOverflow: true,
    };
  }, [displayDeck, visibleCount]);

  const hasItemsToShow = visibleItems.length > 0 || overflowItems.length > 0;

  return {
    filteredDeck,
    visibleItems,
    overflowItems,
    hasOverflow,
    hasItemsToShow,
    showGuestLogin,
    isExpanded,
    toggleExpanded,
    trackView,
    openInvitationModal: () => setOpenInvitation(true),
    openGuestLogin,
    setOpenGuestLogin,
  };
};
